import { Link } from "react-router-dom";
import { Flower2, Sparkles, Flame, Clock, CalendarDays } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { ASSETS } from "@/data/site";

const INCLUSIONS = [
  "Maternal Restoration",
  "Healing Foods",
  "Restorative body tending",
  "Newborn care while mother rests",
  "Home tending",
  "Postpartum planning",
  "\u201CGathering her back in\u201D closing ritual",
];

export default function FeaturedFirstFortyDays() {
  return (
    <section
      className="bg-blush py-24 lg:py-32"
      data-testid="featured-first-forty-days-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <figure
            className="overflow-hidden rounded-3xl border border-espresso/15"
            data-testid="forty-days-masthead"
          >
            <img
              src={ASSETS.honorBath}
              alt="A mother curled in a womb-shaped hollow in the sand beside the sea — the birth of both mother and child."
              className="duotone-warm aspect-[21/9] w-full object-cover"
              data-testid="forty-days-masthead-image"
            />
          </figure>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-12 grid grid-cols-1 gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">
                Our full-spectrum sanctuary
              </p>
              <h2
                className="mt-5 font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl lg:text-6xl"
                data-testid="forty-days-title"
              >
                The First <span className="italic text-sienna">Forty Days</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-cocoa">
                Three visits each week — approximately <span className="font-serif italic text-espresso">55 hours of tending</span>{" "}
                woven around the mother and her little one through the whole fourth trimester.
              </p>

              <dl
                className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2"
                data-testid="forty-days-schedule"
              >
                <div className="rounded-2xl border border-espresso/12 bg-ivory/70 p-5 backdrop-blur-sm">
                  <div className="flex items-center gap-3 text-sienna">
                    <CalendarDays className="h-4 w-4" strokeWidth={1.5} />
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.28em]">
                      Rhythm of the week
                    </dt>
                  </div>
                  <dd className="mt-3 font-serif text-lg leading-snug text-espresso">
                    Monday · Wednesday · Friday
                  </dd>
                </div>
                <div className="rounded-2xl border border-espresso/12 bg-ivory/70 p-5 backdrop-blur-sm">
                  <div className="flex items-center gap-3 text-sienna">
                    <Clock className="h-4 w-4" strokeWidth={1.5} />
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.28em]">
                      Hours of tending
                    </dt>
                  </div>
                  <dd className="mt-3 font-serif text-lg leading-snug text-espresso">
                    Mon &amp; Wed · 10–2 or 12–4
                    <span className="block text-cocoa/85">Fri · 10 AM – 1 PM</span>
                  </dd>
                </div>
              </dl>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link
                  to="/book?service=the-first-forty-days"
                  data-testid="forty-days-book-button"
                  className="rounded-full bg-espresso px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-espresso/85"
                >
                  Enquire about care
                </Link>
                <p className="text-sm text-cocoa/70">
                  <span className="font-serif text-espresso">Custom quote</span>
                  <br />
                  <span>Availability moves with our course schedule</span>
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div
                className="rounded-3xl border border-espresso/12 bg-ivory/70 p-8 backdrop-blur-sm lg:p-12"
                data-testid="forty-days-inclusions"
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sienna/10 text-sienna">
                    <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sienna">
                    This care includes
                  </p>
                </div>
                <h3 className="mt-6 font-serif text-3xl font-light leading-tight text-espresso sm:text-4xl">
                  Everything a mother is meant to be held by
                </h3>
                <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
                  {INCLUSIONS.map((item, idx) => (
                    <li
                      key={item}
                      className="flex items-start gap-4 border-t border-espresso/10 pt-4 text-[15px] leading-relaxed text-espresso/90"
                    >
                      <span className="font-serif text-sm italic text-sienna/80">
                        0{idx + 1}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 grid grid-cols-1 gap-4 border-t border-espresso/10 pt-6 text-sm text-cocoa/85 sm:grid-cols-2">
                  <p className="flex items-start gap-3">
                    <Flame className="mt-0.5 h-4 w-4 shrink-0 text-sienna" strokeWidth={1.5} />
                    <span>
                      The majority of our time together is devoted to <span className="font-serif italic text-espresso">Maternal Restoration</span> —
                      deeply supporting the mother through rest, nourishment, warmth and attentive care.
                      Additional postpartum offerings are gently woven into the remaining time to
                      complement and enrich her recovery.
                    </span>
                  </p>
                  <p className="flex items-start gap-3">
                    <Flower2 className="mt-0.5 h-4 w-4 shrink-0 text-sienna" strokeWidth={1.5} />
                    <span>
                      The final tending closes the passage with the{" "}
                      <span className="font-serif italic text-espresso">"Gathering her back in"</span> ritual.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div
            className="mt-16 grid grid-cols-1 items-stretch overflow-hidden rounded-3xl border border-espresso/15 bg-ivory/70 backdrop-blur-sm lg:mt-24 lg:grid-cols-12"
            data-testid="gathering-ritual-card"
          >
            <figure className="relative lg:col-span-6">
              <img
                src={ASSETS.rebozoRitual}
                alt="Athena tying the rebozo during a closing ritual — 'Gathering her back in.'"
                className="h-full min-h-[420px] w-full object-cover lg:min-h-[560px]"
                data-testid="gathering-ritual-image"
              />
            </figure>
            <div className="flex flex-col justify-center gap-6 p-10 lg:col-span-6 lg:p-14">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sienna/10 text-sienna">
                  <Flower2 className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sienna">
                  Closing ritual · Day forty
                </p>
              </div>
              <h3 className="font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl">
                Gathering her <span className="italic text-sienna">back in</span>
              </h3>
              <p className="font-serif text-base italic text-cocoa/80">
                Postpartum closing ritual — the passage sealed with cloth, warmth and blessing.
              </p>
              <p className="text-[15px] leading-relaxed text-cocoa">
                After the weeks of being warmed, fed and held close, there comes a time to
                gently gather the mother back in. Rebozo binding, the Mother's Rose &amp; Milk
                Honoring Bath, and blessing the rite of passage bring her softly back to
                the everyday rhythm of life.
              </p>
              <ul className="grid grid-cols-1 gap-3 border-t border-espresso/10 pt-6 sm:grid-cols-2">
                {[
                  "Blessing the rite of passage",
                  "Benkung body binding",
                  "Mother's Rose & Milk Honoring Bath",
                  "Womb warmth and healing touch",
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-espresso/85">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sienna" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <figure
            className="relative mt-16 overflow-hidden rounded-3xl border border-espresso/15 lg:mt-24"
            data-testid="sacred-bath-band"
          >
            <img
              src={ASSETS.sacredBath}
              alt="A warm rose and milk honoring bath prepared for the mother."
              className="aspect-[21/7] w-full object-cover"
              data-testid="sacred-bath-image"
            />
            <figcaption className="absolute inset-0 flex items-end bg-gradient-to-tr from-espresso/70 via-espresso/25 to-transparent">
              <div className="p-8 sm:p-12 lg:p-16">
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-cream">
                  Part of the "Gathering her back in" ritual
                </p>
                <p className="mt-3 font-serif text-3xl italic leading-tight text-ivory sm:text-4xl lg:text-5xl">
                  Mother's Rose &amp; Milk Honoring Bath
                </p>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ivory/85">
                  The closing ceremony gathers here — warm water, rose petals
                  and milk, a steeping bath prepared to bless the mother's
                  return to her own tender body.
                </p>
              </div>
            </figcaption>
          </figure>
        </FadeUp>
      </div>
    </section>
  );
}
